/**
 * EmailSidebar — folder list, mailbox avatar picker, tag list.
 * Data comes from store.mailboxes/emailFolders/emailTags signals (bootstrapped on init).
 * Live unread counts come from store.sidebarCounts (ActionCable EmailJsonChannel).
 *
 * Layout:
 *   Left rail: peer mailbox avatars (all kinds, including personal)
 *   Right panel:
 *     1. Compose button
 *     2. Active mailbox/inbox label
 *     3. System folders
 *     4. Custom folders
 *     5. Tags (always visible + create affordance)
 *     6. Settings (collapsible)
 */

import { h } from 'preact'
import { useState, useEffect, useRef } from 'preact/hooks'
import { resolveFolderLabel } from '@tshmanager/email-shared'
import { MailboxAvatar } from './MailboxAvatar.jsx'
import { RequiredAsterisk } from '../shared/RequiredAsterisk.jsx'
import { QuotaBanner } from '../shared/quota/QuotaBanner.jsx'

const SYSTEM_FOLDERS = [
  { key: 'inbox',    icon: 'bi-inbox',         labelKey: 'emails.folders.inbox' },
  { key: 'starred',  icon: 'bi-star',           labelKey: 'emails.folders.starred' },
  { key: 'sent',     icon: 'bi-send',           labelKey: 'emails.folders.sent' },
  { key: 'drafts',   icon: 'bi-file-earmark',   labelKey: 'emails.folders.drafts' },
  { key: 'archived', icon: 'bi-archive',        labelKey: 'emails.folders.archived' },
  { key: 'spam',     icon: 'bi-exclamation-octagon', labelKey: 'emails.folders.spam' },
  { key: 'trash',    icon: 'bi-trash',          labelKey: 'emails.folders.trash' },
]

function InlineRenameForm({ value, onSubmit, onCancel, i18n, withColor, color }) {
  const inputRef = useRef(null)
  const colorRef = useRef(null)

  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.focus()
    el.select()
  }, [])

  function handleSubmit(e) {
    e.preventDefault()
    const next = inputRef.current?.value?.trim() || ''
    if (!next) return
    const nextColor = withColor ? (colorRef.current?.value || color || '#6c757d') : undefined
    onSubmit(next, nextColor)
  }

  return (
    <form class="px-2 py-1 flex-grow-1" onSubmit={handleSubmit} onClick={e => e.stopPropagation()}>
      <div class="input-group input-group-sm flex-nowrap">
        <input
          ref={inputRef}
          type="text"
          class="form-control form-control-sm"
          defaultValue={value}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              e.preventDefault()
              onCancel()
            }
          }}
        />
        {withColor && (
          <input
            ref={colorRef}
            type="color"
            class="form-control form-control-sm form-control-color email-tag-color-input"
            defaultValue={color || '#6c757d'}
            title={i18n?.t('emails.rename_tag') || 'Rename'}
          />
        )}
        <button type="submit" class="btn btn-sm btn-outline-primary flex-shrink-0">
          <i class="bi bi-check" />
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary flex-shrink-0"
          title={i18n?.t('helpers.actions.cancel')}
          onClick={onCancel}
        >
          <i class="bi bi-x" />
        </button>
      </div>
    </form>
  )
}


export function EmailSidebar({ store, i18n, logoUrl = '' }) {
  const activeFolder      = store.activeFolder.value
  const activeMailbox     = store.activeMailbox.value
  const mailboxes         = store.mailboxes.value
  const emailFolders      = store.emailFolders.value
  const emailTags         = store.emailTags.value
  const counts            = store.sidebarCounts.value
  const storageQuota      = store.storageQuota?.value
  const usesStackedNav    = store.usesStackedNav()

  const [settingsOpen, setSettingsOpen] = useState(false)
  // { kind: 'folder'|'tag', id: number }
  const [renaming, setRenaming] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)

  function getFolderCount(key) {
    return counts?.[key] || 0
  }

  function getCustomFolderCount(id) {
    return counts?.[`folder_${id}`] || 0
  }

  function getTagCount(id) {
    return counts?.[`tag_${id}`] || 0
  }

  function handleFolderClick(e, folderKey) {
    e.preventDefault()
    store.setActiveFolder(folderKey)
  }

  function handleMailboxClick(e, mailboxId) {
    e.preventDefault()
    store.setActiveMailbox(mailboxId)
  }

  function setNewFolderInputRequired(formEl, required) {
    const input = formEl?.querySelector('input[name="email_folder[name]"]')
    if (input) input.required = required
  }

  function setNewTagInputRequired(formEl, required) {
    const input = formEl?.querySelector('input[name="email_tag[tag]"]')
    if (input) input.required = required
  }

  async function handleNewFolderSubmit(e) {
    e.preventDefault()
    if (!activeMailbox) {
      alert(i18n?.t('emails.folder_pick_mailbox') || 'Select a mailbox first')
      return
    }
    const name = e.target.elements['email_folder[name]']?.value?.trim()
    if (!name) return
    // Close the form immediately — createFolder inserts an optimistic row.
    const formWrap = e.target.closest('[data-new-folder-form]')
    e.target.reset()
    formWrap?.classList.add('d-none')
    setNewFolderInputRequired(formWrap, false)
    await store.createFolder(name, activeMailbox)
  }

  async function handleNewTagSubmit(e) {
    e.preventDefault()
    if (!activeMailbox) {
      alert(i18n?.t('emails.folder_pick_mailbox') || 'Select a mailbox first')
      return
    }
    const name = e.target.elements['email_tag[tag]']?.value?.trim()
    if (!name) return
    const color = e.target.elements['email_tag[color]']?.value || '#6c757d'
    // Close the form immediately — createTag inserts an optimistic row.
    const formWrap = e.target.closest('[data-new-tag-form]')
    e.target.reset()
    formWrap?.classList.add('d-none')
    setNewTagInputRequired(formWrap, false)
    await store.createTag(name, color, activeMailbox)
  }

  function startRename(kind, id) {
    if (Number(id) < 0) return
    setRenaming({ kind, id: Number(id) })
  }

  async function commitFolderRename(folder, nextName) {
    setRenaming(null)
    if (!nextName || nextName === folder.name) return
    await store.renameFolder(folder.id, nextName)
  }

  async function commitTagRename(tag, nextName, nextColor) {
    setRenaming(null)
    const colorChanged = nextColor && nextColor !== (tag.color || '#6c757d')
    if ((!nextName || nextName === tag.tag) && !colorChanged) return
    await store.renameTag(tag.id, nextName || tag.tag, nextColor)
  }

  // Determine active mailbox/inbox label shown under compose button
  const activeMailboxName = activeMailbox
    ? (mailboxes.find(mb => mb.id === activeMailbox)?.name || '')
    : i18n?.t('emails.all_mailboxes')

  const hasRail = mailboxes.length > 0

  const visibleFolders = activeMailbox
    ? emailFolders.filter(f => f.mailbox_id === activeMailbox)
    : emailFolders

  const visibleTags = activeMailbox
    ? emailTags.filter(t => t.mailbox_id === activeMailbox)
    : emailTags

  // Active folder display name (for mobile header "open list" button)
  const activeFolderDef = SYSTEM_FOLDERS.find(f => f.key === activeFolder)
  const activeFolderLabel = activeFolderDef
    ? i18n?.t(activeFolderDef.labelKey)
    : resolveFolderLabel(activeFolder, { emailFolders: visibleFolders, emailTags: visibleTags })
      ?? activeFolder

  return (
    <div class="email-sidebar-inner">

      {/* Mobile header: app title + open list button */}
      {usesStackedNav && (
        <div class="email-sidebar-mobile-header d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
          <span class="fw-semibold" style="font-size:1rem">
            <i class="bi bi-envelope me-2 text-primary" />
            {i18n?.t('emails.app_title') || 'Levelek'}
          </span>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
            onClick={() => store.showListPanel()}
          >
            <span style="font-size:0.8rem">{activeFolderLabel}</span>
            <i class="bi bi-chevron-right" style="font-size:0.75rem" />
          </button>
        </div>
      )}

      {/* Left: vertical mailbox avatar rail (all peer mailboxes) */}
      {hasRail && (
        <div class={`email-mailbox-rail${logoUrl ? ' email-mailbox-rail--branded' : ''}`}>
          {logoUrl && (
            <img
              src={logoUrl}
              alt=""
              class="email-mailbox-rail-logo"
              width="32"
              height="32"
            />
          )}
          {/* "All mailboxes" button */}
          <button
            type="button"
            class={`mailbox-avatar-btn ${activeMailbox === null ? 'active' : ''}`}
            title={i18n?.t('emails.all_mailboxes')}
            onClick={e => handleMailboxClick(e, null)}
          >
            <i class="bi bi-envelope-open" style="font-size:0.9rem;color:#6c757d" />
          </button>

          {mailboxes.map(mb => (
            <button
              key={mb.id}
              type="button"
              class={`mailbox-avatar-btn ${mb.kind === 'personal' ? 'mailbox-avatar-btn--personal' : ''} ${activeMailbox === mb.id ? 'active' : ''}`}
              title={mb.name}
              onClick={e => handleMailboxClick(e, mb.id)}
            >
              <MailboxAvatar mailbox={mb} />
            </button>
          ))}
        </div>
      )}

      {/* Right: folder / tag panel */}
      <div class="email-sidebar-panel">

        <div class="email-sidebar-panel-header flex-shrink-0">
          {/* Compose button */}
          <button
            type="button"
            class="btn btn-primary w-100 mb-2 d-flex align-items-center gap-2 email-sidebar-compose"
            onClick={() => store.openCompose('new')}
          >
            <i class="bi bi-pencil-square" />
            <span>{i18n?.t('emails.compose')}</span>
          </button>

          {/* Active mailbox / inbox label */}
          {mailboxes.length > 0 && (
            <div class="email-sidebar-mailbox-label" title={activeMailboxName}>
              {activeMailboxName}
            </div>
          )}

          {storageQuota && (
            <QuotaBanner
              quota={storageQuota}
              t={(key, opts) => i18n?.t(key, opts)}
              format="bytes"
              compact={false}
              threshold={0.9}
              className="mt-2"
            />
          )}
        </div>

        <div class="email-sidebar-panel-scroll">
        {/* System folders */}
        <nav class="email-folders">
          {SYSTEM_FOLDERS.map(folder => {
            const count = getFolderCount(folder.key)
            const isActive = activeFolder === folder.key
            return (
              <a
                key={folder.key}
                href={`/emails/inbox?folder=${folder.key}`}
                class={`email-folder-item ${isActive ? 'active' : ''}`}
                data-email-drop-zone=""
                data-drop-folder-key={folder.key}
                onClick={e => handleFolderClick(e, folder.key)}
              >
                <i class={`bi ${folder.icon}`} />
                <span class="flex-grow-1">{i18n?.t(folder.labelKey)}</span>
                <UnreadBadge count={count} id={`sidebar_folder_${folder.key}_count`} />
              </a>
            )
          })}
        </nav>

        {/* Custom folders */}
        <hr class="my-2" />
        <div class="d-flex align-items-center justify-content-between px-2 mb-1">
          <span class="email-sidebar-section-title mb-0">{i18n?.t('emails.custom_folders')}</span>
          <button
            type="button"
            class="btn btn-sm btn-link text-muted p-0"
            title={activeMailbox
              ? (i18n?.t('emails.new_folder') || 'New folder')
              : (i18n?.t('emails.folder_pick_mailbox') || 'Select a mailbox first')}
            disabled={!activeMailbox}
            onClick={e => {
              if (!activeMailbox) return
              const form = e.currentTarget.closest('.d-flex')?.nextElementSibling
              const wasHidden = form?.classList.contains('d-none')
              form?.classList.toggle('d-none')
              setNewFolderInputRequired(form, wasHidden)
            }}
          >
            <i class="bi bi-folder-plus" />
          </button>
        </div>

        {/* New folder form (hidden by default) */}
        <div class="px-2 mb-1 d-none" data-new-folder-form>
          <form onSubmit={handleNewFolderSubmit}>
            <label class="form-label small mb-1">
              {i18n?.t('emails.new_folder')}
              <RequiredAsterisk />
            </label>
            <div class="input-group input-group-sm">
              <input
                type="text"
                name="email_folder[name]"
                class="form-control form-control-sm"
                placeholder={i18n?.t('emails.new_folder')}
              />
              <button type="submit" class="btn btn-sm btn-outline-primary">
                <i class="bi bi-check" />
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                title={i18n?.t('helpers.actions.cancel')}
                onClick={e => {
                  const formWrap = e.currentTarget.closest('[data-new-folder-form]')
                  formWrap?.classList.add('d-none')
                  setNewFolderInputRequired(formWrap, false)
                }}
              >
                <i class="bi bi-x" />
              </button>
            </div>
          </form>
        </div>

        <nav class="email-folders" id="email_custom_folders">
          {visibleFolders.map(folder => {
            const count = getCustomFolderCount(folder.id)
            const isRenaming = renaming?.kind === 'folder' && renaming.id === Number(folder.id)

            if (isRenaming) {
              return (
                <div key={folder.id} class="email-folder-item-wrap d-flex align-items-center">
                  <InlineRenameForm
                    value={folder.name}
                    i18n={i18n}
                    onCancel={() => setRenaming(null)}
                    onSubmit={nextName => commitFolderRename(folder, nextName)}
                  />
                </div>
              )
            }

            const isConfirmingDelete = confirmDelete?.kind === 'folder' && confirmDelete.id === Number(folder.id)
            if (isConfirmingDelete) {
              const confirmMsg = count > 0
                ? i18n?.t('emails.delete_folder_confirm_with_count', { count })
                : i18n?.t('emails.delete_folder_confirm')
              return (
                <div key={folder.id} class="email-folder-item-wrap email-folder-delete-confirm d-flex align-items-center gap-2 px-2 py-1">
                  <span class="small flex-grow-1 text-truncate">{confirmMsg}</span>
                  <button
                    type="button"
                    class="btn btn-sm btn-danger py-0 flex-shrink-0"
                    onClick={async () => {
                      setConfirmDelete(null)
                      await store.deleteFolder(folder.id)
                    }}
                  >
                    {i18n?.t('emails.delete_folder')}
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary py-0 flex-shrink-0"
                    onClick={() => setConfirmDelete(null)}
                  >
                    {i18n?.t('helpers.actions.cancel')}
                  </button>
                </div>
              )
            }

            return (
              <div key={folder.id} class="email-folder-item-wrap d-flex align-items-center">
                <a
                  href={`/emails/inbox?email_folder_id=${folder.id}`}
                  class={`email-folder-item flex-grow-1 ${activeFolder === `folder_${folder.id}` ? 'active' : ''}`}
                  data-email-drop-zone=""
                  data-drop-folder-id={folder.id}
                  data-drop-mailbox-id={folder.mailbox_id}
                  onClick={e => { e.preventDefault(); store.setActiveFolder(`folder_${folder.id}`) }}
                >
                  <i class="bi bi-folder" style={folder.color ? { color: folder.color } : undefined} />
                  <span class="flex-grow-1 text-truncate">{folder.name}</span>
                  <UnreadBadge count={count} id={`sidebar_custom_folder_${folder.id}_count`} />
                </a>
                <div class="dropdown email-folder-actions flex-shrink-0 pe-1">
                  <button
                    class="btn btn-sm btn-link text-muted p-0"
                    type="button"
                    data-bs-toggle="dropdown"
                    data-bs-strategy="fixed"
                    aria-expanded="false"
                    onClick={e => e.stopPropagation()}
                  >
                    <i class="bi bi-three-dots" />
                  </button>
                  <ul class="dropdown-menu dropdown-menu-end shadow-sm">
                    <li>
                      <button
                        class="dropdown-item small"
                        type="button"
                        onClick={e => {
                          e.preventDefault()
                          setConfirmDelete(null)
                          startRename('folder', folder.id)
                        }}
                      >
                        <i class="bi bi-pencil me-2" />
                        {i18n?.t('emails.rename_folder')}
                      </button>
                    </li>
                    <li>
                      <button
                        class="dropdown-item small text-danger"
                        type="button"
                        onClick={e => {
                          e.preventDefault()
                          setRenaming(null)
                          setConfirmDelete({ kind: 'folder', id: folder.id })
                        }}
                      >
                        <i class="bi bi-trash me-2" />
                        {i18n?.t('emails.delete_folder')}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            )
          })}
        </nav>

        {/* Tags — always visible (create affordance even when empty) */}
        <hr class="my-2" />
        <div class="d-flex align-items-center justify-content-between px-2 mb-1">
          <span class="email-sidebar-section-title mb-0">{i18n?.t('emails.tags_label')}</span>
          <button
            type="button"
            class="btn btn-sm btn-link text-muted p-0"
            title={i18n?.t('emails.new_tag')}
            disabled={!activeMailbox}
            onClick={e => {
              if (!activeMailbox) return
              const form = e.currentTarget.closest('.d-flex')?.nextElementSibling
              const wasHidden = form?.classList.contains('d-none')
              form?.classList.toggle('d-none')
              setNewTagInputRequired(form, wasHidden)
            }}
          >
            <i class="bi bi-plus-lg" />
          </button>
        </div>

        {/* New tag form (hidden by default) */}
        <div class="px-2 mb-1 d-none" data-new-tag-form>
          <form onSubmit={handleNewTagSubmit}>
            <label class="form-label small mb-1">
              {i18n?.t('emails.new_tag')}
              <RequiredAsterisk />
            </label>
            <div class="input-group input-group-sm flex-nowrap">
              <input
                type="text"
                name="email_tag[tag]"
                class="form-control form-control-sm"
                placeholder={i18n?.t('emails.new_tag')}
              />
              <input
                type="color"
                name="email_tag[color]"
                class="form-control form-control-sm form-control-color email-tag-color-input"
                defaultValue="#6c757d"
                title={i18n?.t('emails.new_tag')}
              />
              <button type="submit" class="btn btn-sm btn-outline-primary flex-shrink-0">
                <i class="bi bi-check" />
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary flex-shrink-0"
                title={i18n?.t('helpers.actions.cancel')}
                onClick={e => {
                  const formWrap = e.currentTarget.closest('[data-new-tag-form]')
                  formWrap?.classList.add('d-none')
                  setNewTagInputRequired(formWrap, false)
                }}
              >
                <i class="bi bi-x" />
              </button>
            </div>
          </form>
        </div>

        <nav class="email-folders" id="email_tags">
          {visibleTags.map(tag => {
            const count = getTagCount(tag.id)
            const isRenaming = renaming?.kind === 'tag' && renaming.id === Number(tag.id)

            if (isRenaming) {
              return (
                <div key={tag.id} class="email-folder-item-wrap d-flex align-items-center">
                  <InlineRenameForm
                    value={tag.tag}
                    color={tag.color}
                    withColor
                    i18n={i18n}
                    onCancel={() => setRenaming(null)}
                    onSubmit={(nextName, nextColor) => commitTagRename(tag, nextName, nextColor)}
                  />
                </div>
              )
            }

            const isConfirmingTagDelete = confirmDelete?.kind === 'tag' && confirmDelete.id === Number(tag.id)
            if (isConfirmingTagDelete) {
              return (
                <div key={tag.id} class="email-folder-item-wrap email-folder-delete-confirm d-flex align-items-center gap-2 px-2 py-1">
                  <span class="small flex-grow-1 text-truncate">{i18n?.t('emails.delete_tag_confirm')}</span>
                  <button
                    type="button"
                    class="btn btn-sm btn-danger py-0 flex-shrink-0"
                    onClick={async () => {
                      setConfirmDelete(null)
                      await store.deleteTag(tag.id)
                    }}
                  >
                    {i18n?.t('emails.delete_tag')}
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary py-0 flex-shrink-0"
                    onClick={() => setConfirmDelete(null)}
                  >
                    {i18n?.t('helpers.actions.cancel')}
                  </button>
                </div>
              )
            }

            return (
              <div key={tag.id} class="email-folder-item-wrap d-flex align-items-center">
                <a
                  href={`/emails/inbox?tag_ids[]=${tag.id}`}
                  class={`email-folder-item flex-grow-1 ${activeFolder === `tag_${tag.id}` ? 'active' : ''}`}
                  onClick={e => { e.preventDefault(); store.setActiveFolder(`tag_${tag.id}`) }}
                >
                  <i class="bi bi-tag-fill" style={tag.color ? { color: tag.color } : undefined} />
                  <span class="flex-grow-1 text-truncate">{tag.tag}</span>
                  <UnreadBadge count={count} id={`sidebar_tag_${tag.id}_count`} />
                </a>
                <div class="dropdown email-folder-actions flex-shrink-0 pe-1">
                  <button
                    class="btn btn-sm btn-link text-muted p-0"
                    type="button"
                    data-bs-toggle="dropdown"
                    data-bs-strategy="fixed"
                    aria-expanded="false"
                    onClick={e => e.stopPropagation()}
                  >
                    <i class="bi bi-three-dots" />
                  </button>
                  <ul class="dropdown-menu dropdown-menu-end shadow-sm">
                    <li>
                      <button
                        class="dropdown-item small"
                        type="button"
                        onClick={e => {
                          e.preventDefault()
                          setConfirmDelete(null)
                          startRename('tag', tag.id)
                        }}
                      >
                        <i class="bi bi-pencil me-2" />
                        {i18n?.t('emails.rename_tag') || i18n?.t('emails.rename_folder')}
                      </button>
                    </li>
                    <li>
                      <button
                        class="dropdown-item small text-danger"
                        type="button"
                        onClick={e => {
                          e.preventDefault()
                          setRenaming(null)
                          setConfirmDelete({ kind: 'tag', id: tag.id })
                        }}
                      >
                        <i class="bi bi-trash me-2" />
                        {i18n?.t('emails.delete_tag')}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            )
          })}
        </nav>

        {/* Settings section (collapsible) */}
        <hr class="my-2" />
        {activeMailbox && store.canImapImport?.value && (
          <a
            href={`/mailboxes/${activeMailbox}/imap_imports/new`}
            class="email-folder-item"
            data-turbo-frame="_top"
          >
            <i class="bi bi-cloud-download" />
            <span>{i18n?.t('emails.imap_import')}</span>
          </a>
        )}
        <button
          class="email-folder-item w-100 text-start border-0 bg-transparent"
          onClick={() => setSettingsOpen(o => !o)}
        >
          <i class="bi bi-gear" />
          <span class="flex-grow-1">{i18n?.t('emails.settings')}</span>
          <i class={`bi ${settingsOpen ? 'bi-chevron-up' : 'bi-chevron-down'} small`} />
        </button>
        {settingsOpen && (
          <div class="ps-3">
            <a href="/emails/statistics" class="email-folder-item" data-turbo-frame="_top">
              <i class="bi bi-bar-chart-line" />
              <span>{i18n?.t('emails.statistics.sidebar_link')}</span>
            </a>
            <button
              type="button"
              class={`email-folder-item ${store.rulesOpen.value ? 'active' : ''}`}
              onClick={() => store.openRules(activeMailbox)}
            >
              <i class="bi bi-funnel" />
              <span>{i18n?.t('emails.email_rules.sidebar_link')}</span>
            </button>
            <a href="/emails/message_trace" class="email-folder-item" data-turbo-frame="_top">
              <i class="bi bi-search" />
              <span>{i18n?.t('emails.message_trace.sidebar_link')}</span>
            </a>
            <a href="/mailboxes" class="email-folder-item" data-turbo-frame="_top">
              <i class="bi bi-mailbox" />
              <span>{i18n?.t('emails.mailbox_settings')}</span>
            </a>
          </div>
        )}

        </div>{/* end .email-sidebar-panel-scroll */}

      </div>{/* end .email-sidebar-panel */}

    </div>
  )
}

function UnreadBadge({ count, id }) {
  const n = Number(count) || 0
  if (n <= 0) {
    return <span id={id} class="badge bg-secondary-subtle text-secondary rounded-pill small d-none" />
  }
  return (
    <span id={id} class="badge bg-secondary-subtle text-secondary rounded-pill small flex-shrink-0">
      {n}
    </span>
  )
}
